import { useEffect, useRef, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatEventType } from '../lib/task-events';
import type { LatLng, TrailPoint } from '../lib/task-trail';
import { boundsOf } from '../lib/task-trail';

/**
 * Peta jejak tugas — HANYA BACA.
 *
 * Tiga keputusan diwarisi dari `MapPicker`, dengan alasan yang sama: Leaflet polos tanpa
 * `react-leaflet` (satu dependensi lebih sedikit, tak ada risiko React 19), lingkaran bukan pin
 * bawaan (berkas gambar penanda Leaflet sering rusak jalurnya setelah bundling), dan dimuat hanya
 * di layar ini supaya ubin peta tidak dibayar oleh layar yang tidak memakainya.
 *
 * Satu keputusan yang khusus di sini: **garisnya PUTUS-PUTUS.**
 *
 * Garis mulus adalah janji bahwa kami tahu jalan yang ditempuh. Kami tidak tahu — yang tersimpan
 * hanya titik pada tiap transisi, dan di antara dua titik kurir bisa lewat mana saja. Garis putus
 * mengatakan "ada jarak yang tidak kami lihat" tanpa satu kalimat pun, kepada orang yang tidak
 * akan membaca catatan kaki.
 */
export function TrailMap({
  points,
  destination,
}: {
  readonly points: readonly TrailPoint[];
  readonly destination: LatLng | null;
}): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    if (mapRef.current === null) {
      const map = L.map(container, {
        // Jejak dibaca, bukan dijelajahi. Menonaktifkan gulir-zoom mencegah halaman "tersangkut"
        // saat orang menggulir layar detail dengan jari melintas di atas peta.
        scrollWheelZoom: false,
        attributionControl: true,
      });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);

      /*
       * Panel khusus supaya penanda TUJUAN berada DI BAWAH titik kejadian.
       *
       * Urutan `addTo` tidak cukup, dan itu yang membuat masalahnya tidak kentara: di Leaflet,
       * `L.marker` selalu hidup di `markerPane` (z-index 600) sedangkan titik lingkaran adalah
       * vektor di `overlayPane` (400). Marker karena itu SELALU menutupi titik, berapa pun
       * urutannya ditambahkan.
       *
       * Akibatnya bukan teoretis: kurir hampir selalu menekan "Tiba" lalu "Selesai" di tempat
       * yang sama, tepat di tujuan — jadi kotak tujuan menutupi DUA titik terakhir pada hampir
       * setiap tugas. Yang tertutup justru informasinya; tujuan hanya rujukan.
       */
      map.createPane('tujuan').style.zIndex = '350';

      mapRef.current = map;
      layerRef.current = L.layerGroup().addTo(map);
    }

    const map = mapRef.current;
    const layer = layerRef.current;
    if (map === null || layer === null) return;
    layer.clearLayers();

    const garis = points.map((p) => [p.latitude, p.longitude] as [number, number]);
    if (garis.length >= 2) {
      L.polyline(garis, { color: '#0152f4', weight: 3, opacity: 0.7, dashArray: '6 8' }).addTo(
        layer,
      );
    }

    points.forEach((titik, indeks) => {
      // Lingkaran akurasi digambar LEBIH DULU supaya ia berada di bawah titiknya.
      if (typeof titik.accuracyMeters === 'number' && titik.accuracyMeters > 0) {
        L.circle([titik.latitude, titik.longitude], {
          radius: titik.accuracyMeters,
          color: '#0152f4',
          weight: 1,
          opacity: 0.25,
          fillOpacity: 0.06,
        }).addTo(layer);
      }
      L.circleMarker([titik.latitude, titik.longitude], {
        radius: 9,
        color: '#ffffff',
        weight: 2,
        fillColor: '#0152f4',
        fillOpacity: 1,
      })
        .bindTooltip(`${indeks + 1}. ${formatEventType(titik.eventType)}`, { direction: 'top' })
        .addTo(layer);
    });

    if (destination !== null) {
      // Tujuan dibedakan BENTUKNYA, bukan hanya warnanya — kotak, bukan lingkaran. Pada layar
      // terang di bawah matahari, beda warna paling cepat hilang.
      // `pane: 'tujuan'` menaruhnya di bawah titik kejadian — alasannya di komentar panel di atas.
      L.marker([destination.latitude, destination.longitude], {
        pane: 'tujuan',
        icon: L.divIcon({
          className: '',
          html: '<div class="trail-destination" aria-hidden="true"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        }),
      })
        .bindTooltip('Tujuan', { direction: 'top' })
        .addTo(layer);
    }

    const kotak = boundsOf(points, destination);
    if (kotak !== null) {
      map.fitBounds(
        L.latLngBounds([kotak.south, kotak.west], [kotak.north, kotak.east]),
        // `maxZoom` menahan peta agar tidak menyelam ke tingkat jalan ketika seluruh titik nyaris
        // berimpit — tugas yang diterima dan diselesaikan di tempat yang sama akan tampak seperti
        // kesalahan peta tanpa ini.
        { padding: [24, 24], maxZoom: 17 },
      );
    }
  }, [points, destination]);

  useEffect(
    () => () => {
      mapRef.current?.remove();
      mapRef.current = null;
      layerRef.current = null;
    },
    [],
  );

  return <div ref={containerRef} className="map-picker" />;
}
